import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';

export interface OrganizationProfile {
  id: string;
  organization_id: string;
  industry?: string | null;
  company_size?: string | null;
  founded_year?: number | null;
  headquarters_location?: string | null;
  street_address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  country?: string | null;
  website_url?: string | null;
  description?: string | null;
  logo_url?: string | null;
  annual_revenue_range?: string | null;
  number_of_locations?: number | null;
  parent_company?: string | null;
  stock_symbol?: string | null;
  duns_number?: string | null;
  annual_procurement_volume?: string | null;
  vendor_requirements: any;
  preferred_payment_terms?: string | null;
  typical_project_types: any;
  is_public_profile: boolean;
  public_description?: string | null;
  contact_email?: string | null;
  accounts_payable_email?: string | null;
  social_media_links: any;
  created_at: string;
  updated_at: string;
}

export interface OrganizationProfileCompletion {
  id: string;
  organization_id: string;
  basic_info_complete: boolean;
  business_info_complete: boolean;
  member_info_complete: boolean;
  overall_percentage: number;
  created_at: string;
  updated_at: string;
}

export const useOrganizationProfile = () => {
  const [profile, setProfile] = useState<OrganizationProfile | null>(null);
  const [completion, setCompletion] = useState<OrganizationProfileCompletion | null>(null);
  const [organizationName, setOrganizationName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Fetch organization profile
  const fetchProfile = async () => {
    try {
      setLoading(true);
      
      // Get user's organization ID
      const user = await supabase.auth.getUser();
      if (!user.data.user) throw new Error('Not authenticated');

      const { data: userProfile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.data.user.id)
        .maybeSingle();

      if (!userProfile?.organization_id) {
        // No organization yet: return gracefully without error
        setOrganizationName('');
        setProfile(null);
        setCompletion(null);
        setLoading(false);
        return;
      }

      // Fetch organization name
      const { data: orgData } = await supabase
        .from('organizations')
        .select('name')
        .eq('id', userProfile.organization_id)
        .single();

      if (orgData) {
        setOrganizationName(orgData.name);
      }

      const { data: profileData, error: profileError } = await supabase
        .from('organization_profiles')
        .select('*')
        .eq('organization_id', userProfile.organization_id)
        .maybeSingle();

      if (profileError) throw profileError;
      setProfile(profileData as OrganizationProfile);

      if (profileData) {
        // Fetch completion status
        const { data: compData, error: compError } = await supabase
          .from('organization_profile_completion')
          .select('*')
          .eq('organization_id', userProfile.organization_id)
          .maybeSingle();

        if (compError) throw compError;
        setCompletion(compData as OrganizationProfileCompletion);
      }
    } catch (err: any) {
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to fetch organization profile",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Create organization profile
  const createProfile = async (profileData: Partial<OrganizationProfile>) => {
    try {
      const user = await supabase.auth.getUser();
      if (!user.data.user) throw new Error('Not authenticated');

      const { data: userProfile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.data.user.id)
        .maybeSingle();

      if (!userProfile?.organization_id) {
        throw new Error('User must be part of an organization');
      }

      const { data, error } = await supabase
        .from('organization_profiles')
        .insert({
          organization_id: userProfile.organization_id,
          is_public_profile: false,
          vendor_requirements: {},
          typical_project_types: [],
          social_media_links: {},
          ...profileData,
        })
        .select('*')
        .single();

      if (error) throw error;
      
      setProfile(data as OrganizationProfile);
      
      // Create completion tracking
      await supabase
        .from('organization_profile_completion')
        .insert({
          organization_id: userProfile.organization_id,
          basic_info_complete: false,
          business_info_complete: false,
          member_info_complete: false,
          overall_percentage: 0,
        });

      // Update completion status with the new profile data
      await updateCompletionStatus(userProfile.organization_id, data as OrganizationProfile);

      toast({
        title: "Success",
        description: "Organization profile created successfully",
      });
      
      return data;
    } catch (err: any) {
      console.error('Error creating organization profile:', err);
      toast({
        title: "Error",
        description: err.message || "Failed to create organization profile",
        variant: "destructive",
      });
      throw err;
    }
  };

  // Update organization profile (owner only)
  const updateProfile = async (updates: Partial<OrganizationProfile>) => {
    try {
      if (!profile) throw new Error('No profile to update');

      // Check if user is organization owner
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: userProfile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.id)
        .maybeSingle();

      const { data: roleData } = await supabase
        .from('organization_user_roles')
        .select('org_role')
        .eq('user_id', user.id)
        .eq('organization_id', userProfile?.organization_id)
        .single();

      if (roleData?.org_role !== 'owner') {
        throw new Error('Only organization owners can update company profile');
      }

      const { data, error } = await supabase
        .from('organization_profiles')
        .update(updates)
        .eq('id', profile.id)
        .select('*')
        .single();

      if (error) throw error;
      
      setProfile(data as OrganizationProfile);
      await updateCompletionStatus(profile.organization_id, data as OrganizationProfile);
      
      // Track profile update
      analytics.track({
        name: ANALYTICS_EVENTS.PROFILE_UPDATED,
        category: 'engagement',
        properties: {
          profile_type: 'organization',
        },
      });

      toast({
        title: "Success",
        description: "Organization profile updated successfully",
      });
      
      return data;
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to update organization profile",
        variant: "destructive",
      });
      throw err;
    }
  };

  // Update completion status
  const updateCompletionStatus = async (organizationId: string, updatedProfile?: Partial<OrganizationProfile>) => {
    try {
      // Use the updated profile if provided, otherwise fall back to state
      const currentProfile = updatedProfile || profile;
      
      // Calculate completion percentages
      const basicInfoComplete = !!(currentProfile?.industry && currentProfile?.company_size && currentProfile?.description);
      const businessInfoComplete = !!(
        currentProfile?.street_address && 
        currentProfile?.city && 
        currentProfile?.state && 
        currentProfile?.zip_code
      );
      
      // Check if organization has members
      const { data: members } = await supabase
        .from('organization_user_roles')
        .select('id')
        .eq('organization_id', organizationId);
      
      const memberInfoComplete = (members && members.length > 0);
      
      const completedSections = [
        basicInfoComplete,
        businessInfoComplete,
        memberInfoComplete
      ].filter(Boolean).length;
      
      const overallPercentage = Math.round((completedSections / 3) * 100);

      const { data, error } = await supabase
        .from('organization_profile_completion')
        .upsert({
          organization_id: organizationId,
          basic_info_complete: basicInfoComplete,
          business_info_complete: businessInfoComplete,
          member_info_complete: memberInfoComplete,
          overall_percentage: overallPercentage,
        }, {
          onConflict: 'organization_id'
        })
        .select('*')
        .single();

      if (error) throw error;
      setCompletion(data as OrganizationProfileCompletion);
    } catch (err: any) {
      console.error('Failed to update completion status:', err);
    }
  };

  // Add project type
  const addProjectType = async (projectType: string) => {
    try {
      if (!profile) throw new Error('No profile found');

      const currentTypes = Array.isArray(profile.typical_project_types) 
        ? profile.typical_project_types 
        : [];
      
      if (!currentTypes.includes(projectType)) {
        const updatedTypes = [...currentTypes, projectType];
        await updateProfile({ typical_project_types: updatedTypes });
      }
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to add project type",
        variant: "destructive",
      });
      throw err;
    }
  };

  // Remove project type
  const removeProjectType = async (projectType: string) => {
    try {
      if (!profile) throw new Error('No profile found');

      const currentTypes = Array.isArray(profile.typical_project_types) 
        ? profile.typical_project_types 
        : [];
      
      const updatedTypes = currentTypes.filter(type => type !== projectType);
      await updateProfile({ typical_project_types: updatedTypes });
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to remove project type",
        variant: "destructive",
      });
      throw err;
    }
  };

  // Toggle public visibility
  const togglePublicVisibility = async () => {
    try {
      if (!profile) throw new Error('No profile found');
      
      await updateProfile({ is_public_profile: !profile.is_public_profile });
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to update visibility settings",
        variant: "destructive",
      });
      throw err;
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  return {
    profile,
    completion,
    organizationName,
    loading,
    error,
    fetchProfile,
    createProfile,
    updateProfile,
    addProjectType,
    removeProjectType,
    togglePublicVisibility,
  };
};